import {
  Alerts,
  AlertsContext,
  Box,
  Button,
  Flex,
  Form,
  ModalContext,
  Text,
  useFormState,
  useFormValidation,
} from "dash-ui";
import { isUrlValid, SalesGalleryStatusesEnum, WeekDays } from "dash-common";
import { FormEvent, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  GetPropertyByIdQuery,
  useSetAdvancedPropertyDetailsMutation,
} from "gql/generated.graphql";
import { ChangesConfirmationModal } from "containers/properties/components/ChangesConfirmationModal";

import {
  FormFields,
  FormFieldsList,
} from "./AdvancedPropertyDetails.constants";
import { compareAdvancedPropertyData } from "./AdvancedPropertyDetails.helpers";
import { TFormHandlers } from "./AdvancedPropertyDetails.interfaces";
import BuildingDetailsFormElements from "./components/BuildingDetailsFormElements/BuildingDetailsFormElements";
// import DescriptionFormElements from './components/DescriptionFormElements/DescriptionFormElements';
import SalesGalleryLocalHours from "./components/SalesGalleryLocalHours/SalesGalleryLocalHours";
import SalesGalleryAddress from "./components/SalesGalleryAddress/SalesGalleryAddress";
import VirtualToursSection from "./components/VirtualToursSection/VirtualToursSection";
import { TopBarWrapper } from "./AdvancedPropertyDetails.styles";
import { PropertyDescriptionSection } from "v2/components/property-description-section";
import { camelCase } from "lodash";

const AdvancedPropertyDetails = ({
  property,
}: {
  property: GetPropertyByIdQuery["getPropertyById"];
}) => {
  const { t } = useTranslation(["common", "pages", "forms"]);
  const { toggleAlert } = useContext(AlertsContext);
  const { toggleModal, setPayload } = useContext(ModalContext);
  const [validateVirtualTours, setValidateVirtualTours] = useState(false);

  const [formState, stateHandlers, [formOnSubmit, formOnError, formOnSuccess]] =
    useFormState(FormFieldsList, property);
  const [formErrors, , errorHandlers] = useFormValidation(FormFieldsList);
  const formRef = useRef<HTMLFormElement>(null);
  const formHandlers: TFormHandlers = FormFieldsList.reduce(
    (memo, field, index) => ({
      ...memo,
      [field]: {
        setState: stateHandlers[index],
        setError: errorHandlers[index],
      },
    }),
    {}
  );
  const [setAdvancedPropertyMutation, { loading }] =
    useSetAdvancedPropertyDetailsMutation();

  // Initialize salesGalleryHours if available
  useEffect(() => {
    if (
      property.salesGalleryHours &&
      formHandlers[FormFields.Sales_Gallery_Hours]
    ) {
      formHandlers[FormFields.Sales_Gallery_Hours].setState(
        property.salesGalleryHours
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property]);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    formOnSubmit();

    const input: any = Object.values(FormFields).reduce(
      (memo, key) => {
        return { ...memo, [key]: formState[key] };
      },
      { propertyId: property._id }
    );

    if (input.salesGalleryHours) {
      input.salesGalleryHours = Object.values(WeekDays).reduce(
        (memo: any, key: string) => {
          const { to, from } = input.salesGalleryHours[key] || {};

          return { ...memo, ...(to && from && { [key]: { to, from } }) };
        },
        {}
      );
    }

    // Validate virtual tour links before showing the changelog modal
    if (input.virtualTourLinks && input.virtualTourLinks.length > 0) {
      // Check for invalid URLs
      const invalidUrls = input.virtualTourLinks.filter(
        (link: any) => link.url && link.url.trim() !== "" && !isUrlValid(link.url)
      );
      
      if (invalidUrls.length > 0) {
        setValidateVirtualTours(true);
        formOnError(t("forms:validations.invalidUrlsBeforeSaving"));
        setTimeout(() => setValidateVirtualTours(false), 100);
        return;
      }

      // Check for labels without URLs
      const entriesWithLabelOnly = input.virtualTourLinks.filter(
        (link: any) => (!link.url || link.url.trim() === "") && link.label && link.label.trim() !== ""
      );
      
      if (entriesWithLabelOnly.length > 0) {
        setValidateVirtualTours(true);
        formOnError(t("forms:validations.labelsWithoutUrls"));
        setTimeout(() => setValidateVirtualTours(false), 100);
        return;
      }
    }

    const updateAdvancedProperty = async (noChangelog: boolean) => {
      try {
        // Clean virtual tour links - validation was already done before modal
        if (input.virtualTourLinks && input.virtualTourLinks.length > 0) {
          // Filter out completely empty entries and remove __typename
          input.virtualTourLinks = input.virtualTourLinks
            .filter((link: any) => link.url && link.url.trim() !== "")
            .map(({ __typename, ...link }: any) => link);
        }

        // Update advanced property details including virtual tours
        const result = await setAdvancedPropertyMutation({
          variables: { input: { propertyDetails: input, noChangelog } },
        });

        // Clean up the form state after save - use the actual saved data
        if (result.data?.setAdvancedPropertyDetails?.virtualTourLinks) {
          const savedLinks = result.data.setAdvancedPropertyDetails.virtualTourLinks;
          if (formHandlers[FormFields.Virtual_Tour_Links]) {
            formHandlers[FormFields.Virtual_Tour_Links].setState(savedLinks);
          }
        } else if (formHandlers[FormFields.Virtual_Tour_Links]) {
          // If no virtual tours were saved, clear the form state
          formHandlers[FormFields.Virtual_Tour_Links].setState([]);
        }

        formOnSuccess();
        toggleAlert(Alerts.ChangesSaved, true);
      } catch (error: any) {
        formOnError(
          t(`common:serverMessages.${error.message}`) || error.message
        );
      }
    };

    setPayload({ updateWithoutChangelog: updateAdvancedProperty });
    toggleModal(true, "CreateChangeLogModal");
  };

  const isEqual = compareAdvancedPropertyData(property, formState);

  // Check if all virtual tour URLs are valid
  const hasInvalidUrls = () => {
    const virtualTourLinks = formState[FormFields.Virtual_Tour_Links] || [];
    return virtualTourLinks.some(
      (link: any) => link.url && !isUrlValid(link.url)
    );
  };

  const handleSelectInputs = (fieldName: FormFields) => (value?: string) => {
    formHandlers[fieldName].setState(value);
  };

  return (
    <Flex pb={6} flexDirection="column" w="100%">
      <ChangesConfirmationModal condition={isEqual} />
      <Form w="100%" ref={formRef} onSubmit={onSubmit}>
        <TopBarWrapper
          pos="sticky"
          top={262}
          alignItems="center"
          justifyContent="flex-end"
          pr={6}
          pl={2}
          py={2}
          mt={4}
          mb={4}
          ml={6}
          mr={0}
          style={{
            float: "right",
            zIndex: 5,
          }}
        >
          <Button
            px={5.5}
            medium
            type="submit"
            disabled={loading || isEqual || hasInvalidUrls()}
            isLoading={loading}
            onClick={() => {
              if (formRef.current) {
                formRef.current.requestSubmit();
              }
            }}
          >
            {t("common:labels.saveChanges")}
          </Button>
        </TopBarWrapper>
        <Box mx={6} mt={4} mb={4}>
          <Text lg>{t("common:labels.description")}</Text>
        </Box>
        {/* <DescriptionFormElements
          formErrors={formErrors}
          formHandlers={formHandlers}
          formState={formState}
        /> */}
        <PropertyDescriptionSection
          data={{
            highlights: formState["projectDetails"],
            overview: formState["overview"],
            brokerCommission: formState["brokerCommission"],
            setHighlights: (value) =>
              formHandlers["projectDetails"].setState(value),
            setOverview: (value) => formHandlers["overview"].setState(value),
            setBrokerCommission: (value) =>
              formHandlers["brokerCommission"].setState(value),
          }}
        />
        <Box mx={6} mt={4}>
          <Text lg>{t("pages:propertyPage.buildingDetails")}</Text>
        </Box>
        <BuildingDetailsFormElements
          formErrors={formErrors}
          formHandlers={formHandlers}
          formState={formState}
        />
        <VirtualToursSection
          virtualTourLinks={formState[FormFields.Virtual_Tour_Links] || []}
          onVirtualTourLinksChange={
            formHandlers[FormFields.Virtual_Tour_Links]?.setState
          }
          validateOnSave={validateVirtualTours}
        />
        <Box mx={6} mt={12}>
          <Text lg>{t("pages:propertyPage.salesGalleryHours")}</Text>
        </Box>
        <Box mx={6} mt={4}>
          <Form.Group w="100%">
            <Form.Label>{t("labels.salesGalleryStatus")}</Form.Label>
            <Form.Select
              isSearchable={false}
              menuPortalZIndex="auto"
              w="100%"
              h="46px"
              value={formState?.salesGalleryStatus || ""}
              options={Object.entries(SalesGalleryStatusesEnum)
                .map(([label, value]) => ({
                  label: t(
                    `forms:labels.salesGalleryStatuses.${camelCase(label)}`
                  ),
                  value,
                }))
                .sort((a, b) => a.label.localeCompare(b.label))}
              placeholder={t("forms:placeholders.selectOne")}
              onChanged={handleSelectInputs(FormFields.Sales_Gallery_Status)}
            />
          </Form.Group>
        </Box>
        {formState?.salesGalleryStatus &&
          formState.salesGalleryStatus !==
            SalesGalleryStatusesEnum.NO_INFORMATION && (
            <SalesGalleryAddress
              value={formState.salesGalleryAddress || ""}
              onChange={(value) =>
                formHandlers[FormFields.Sales_Gallery_Address].setState(value)
              }
            />
          )}
        {formState?.salesGalleryStatus ===
          SalesGalleryStatusesEnum.HOURS_AVAILABLE && (
          <SalesGalleryLocalHours
            setSalesGalleryHours={(value) =>
              formHandlers[FormFields.Sales_Gallery_Hours].setState(value)
            }
            setSalesGalleryHoursError={(value) =>
              formHandlers[FormFields.Sales_Gallery_Hours].setError(value)
            }
            formErrors={formErrors}
            formState={formState}
          />
        )}
      </Form>
    </Flex>
  );
};

export default AdvancedPropertyDetails;
